// Regras de hierarquia de ouvidorias — espelha o backend
// (ver src/Context/hierarquia-ouvidorias.md e openapiUrbNet.json).

/** Valores de `instituicao` conforme serializados pela API (Response.Ouvidoria). */
export const INSTITUICAO = {
  Prefeitura: 'Prefeitura',
  Setorial: 'Setorial',
  AssociacaoDeMoradores: 'Associação de Moradores',
  AssociacaoSupervisora: 'Associação Supervisora',
} as const;

export type Instituicao = (typeof INSTITUICAO)[keyof typeof INSTITUICAO];

/** Valores aceitos pelo filtro `instituicao` (QueryFilter.Ouvidorias.Instituicao). */
export const INSTITUICAO_FILTRO = {
  Prefeitura: 'Prefeitura',
  Setorial: 'Setorial',
  AssociacaoDeMoradores: 'AssociacaoDeMoradores',
  AssociacaoSupervisora: 'AssociacaoSupervisora',
} as const;

export type InstituicaoFiltro = (typeof INSTITUICAO_FILTRO)[keyof typeof INSTITUICAO_FILTRO];

/** Valores de `status` conforme serializados pela API (Ouvidoria.Status). */
export const STATUS_OUVIDORIA = {
  AguardandoAprovacao: 'Aguardando Aprovação',
  Aprovada: 'Aprovada',
  Reprovada: 'Reprovada',
} as const;

export type StatusOuvidoria = (typeof STATUS_OUVIDORIA)[keyof typeof STATUS_OUVIDORIA];

/** Valores aceitos pelo filtro `status` (QueryFilter.Ouvidorias.Status). */
export const STATUS_OUVIDORIA_FILTRO = {
  AguardandoAprovacao: 'AguardandoAprovacao',
  Aprovada: 'Aprovada',
  Reprovada: 'Reprovada',
} as const;

export type StatusOuvidoriaFiltro =
  (typeof STATUS_OUVIDORIA_FILTRO)[keyof typeof STATUS_OUVIDORIA_FILTRO];

/** Shape mínimo para avaliação das regras de hierarquia. */
export interface OuvidoriaHierarquia {
  geral?: boolean;
  arquivada?: boolean;
  status?: string | null;
  instituicao?: string | null;
  supervisoraId?: string | null;
  naturezaJuridica?: string | null;
  atividadePrincipal?: string | null;
}

const normalizar = (valor?: string | null) =>
  (valor ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();

/** CNAE 9430-8/00 — Atividades de associações de defesa de direitos sociais. */
const CNAE_DEFESA_DIREITOS_DIGITOS = '9430800';

/**
 * Espelha `Ouvidoria.ElegivelAssociacaoSupervisora` do backend:
 * natureza jurídica contém "Associação Privada" E atividade principal contém
 * "defesa de direitos sociais" ou o CNAE 9430-8/00.
 */
export function elegivelAssociacaoSupervisora(
  naturezaJuridica?: string | null,
  atividadePrincipal?: string | null,
): boolean {
  if (!normalizar(naturezaJuridica).includes('associacao privada')) {
    return false;
  }
  const atividade = normalizar(atividadePrincipal);
  if (atividade.includes('defesa de direitos sociais')) {
    return true;
  }
  // CNAE pode vir formatado ("9430-8/00") ou só dígitos ("9430800").
  return atividade.replace(/\D/g, '').includes(CNAE_DEFESA_DIREITOS_DIGITOS);
}

/** `instituicao === "Associação Supervisora"` (ou flag explícita de criação). */
export function isAssociacaoSupervisora(ouvidoria: OuvidoriaHierarquia): boolean {
  return ouvidoria.instituicao === INSTITUICAO.AssociacaoSupervisora;
}

/**
 * `PodeSupervisionar` do backend: `Ativa && Aprovada && (Geral || AssociacaoSupervisora)`.
 * `arquivada === true` indica inativa; status ausente não bloqueia (payload minimal).
 */
export function podeSupervisionar(ouvidoria: OuvidoriaHierarquia | null | undefined): boolean {
  if (!ouvidoria) return false;
  if (ouvidoria.arquivada === true) return false;
  if (ouvidoria.status != null && ouvidoria.status !== STATUS_OUVIDORIA.Aprovada) {
    return false;
  }
  return ouvidoria.geral === true || isAssociacaoSupervisora(ouvidoria);
}

/** Rótulo amigável da instituição para exibição. */
export function instituicaoLabel(instituicao?: string | null): string {
  switch (instituicao) {
    case INSTITUICAO.Prefeitura:
      return 'Prefeitura';
    case INSTITUICAO.Setorial:
      return 'Setorial';
    case INSTITUICAO.AssociacaoDeMoradores:
      return 'Associação de Moradores';
    case INSTITUICAO.AssociacaoSupervisora:
      return 'Associação Supervisora';
    default:
      return instituicao || '—';
  }
}
